import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import AdminNav from '../components/AdminNav';
import TemplateCardPreview from '../components/TemplateCardPreview';
import { ICON_LIBRARY, getIconComponent, getIconGroups } from '../data/iconLibrary';
import { 
  Plus, Edit, Trash2, Grid3X3, Palette, CreditCard,
  X, Upload, Loader2, Search, ArrowLeft,
  Check, XCircle, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${API_URL}${url}`;
};

// ====== Icon Picker Component ======
const IconPicker = ({ value, onChange }) => {
  const [search, setSearch] = useState('');
  const groups = getIconGroups();
  const filtered = search
    ? ICON_LIBRARY.filter(i => i.label.toLowerCase().includes(search.toLowerCase()) || i.id.includes(search.toLowerCase()) || i.group.toLowerCase().includes(search.toLowerCase()))
    : ICON_LIBRARY;
  const filteredGroups = {};
  filtered.forEach(i => { if (!filteredGroups[i.group]) filteredGroups[i.group] = []; filteredGroups[i.group].push(i); });

  return (
    <div>
      <div className="relative mb-2">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search icons..." className="pl-8 h-8 text-sm" data-testid="icon-search-input" />
      </div>
      <div className="max-h-48 overflow-y-auto border rounded-lg p-2 bg-gray-50">
        {Object.entries(filteredGroups).map(([group, icons]) => (
          <div key={group} className="mb-2">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-1">{group}</p>
            <div className="flex flex-wrap gap-1">
              {icons.map(icon => {
                const IconComp = icon.component;
                return (
                  <button key={icon.id} type="button" title={icon.label}
                    className={`w-8 h-8 flex items-center justify-center rounded-md transition-all ${value === icon.id ? 'bg-amber-100 ring-2 ring-amber-500 text-amber-700' : 'hover:bg-gray-200 text-gray-600'}`}
                    onClick={() => onChange(icon.id)} data-testid={`icon-option-${icon.id}`}
                  >
                    <IconComp className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No icons match your search</p>}
      </div>
      {value && (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
          {(() => { const IC = getIconComponent(value); return <IC className="w-4 h-4 text-amber-600" />; })()}
          <span>Selected: <strong>{ICON_LIBRARY.find(i => i.id === value)?.label || value}</strong></span>
        </div>
      )}
    </div>
  );
};

const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];

const AdminCardDesigner = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const frontFileRef = useRef(null);
  const backFileRef = useRef(null);
  
  // Check admin access — only redirect when used as standalone page
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
  
  useEffect(() => {
    if (!embedded && user && !isAdmin) {
      toast({ title: "Access Denied", description: "Only admin can access this page", variant: "destructive" });
      navigate('/');
    }
  }, [user, isAdmin, navigate, toast, embedded]);
  
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  // Two-view: null = categories grid, categoryId = templates for that category
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Category Dialog
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '', icon: 'briefcase', description: '', display_order: 0, is_active: true, thumbnail_image: null
  });
  
  // Template Dialog
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '', category_id: '', price: '', description: '',
    front_image: '', back_image: '', is_active: true
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadData(); }, []); // Load immediately on mount — APIs are public

  const loadData = async () => {
    try {
      const [catRes, tempRes] = await Promise.all([
        fetch(`${API_URL}/api/card-designer/categories?active_only=false&include_empty=true`),
        fetch(`${API_URL}/api/card-designer/templates?active_only=false`)
      ]);
      if (catRes.ok) setCategories(await catRes.json());
      if (tempRes.ok) setTemplates(await tempRes.json());
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Derived: templates for the selected category
  const categoryTemplates = selectedCategory
    ? templates.filter(t => t.category_id === selectedCategory.id)
    : [];

  // =================== CATEGORY ===================
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name, icon: category.icon || 'briefcase',
        description: category.description || '', display_order: category.display_order || 0,
        is_active: category.is_active !== false, thumbnail_image: category.thumbnail_image || null
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', icon: 'briefcase', description: '', display_order: categories.length, is_active: true, thumbnail_image: null });
    }
    setShowCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) { toast({ title: "Missing fields", description: "Category name is required", variant: "destructive" }); return; }
    try {
      const token = await user.getIdToken();
      const method = editingCategory ? 'PUT' : 'POST';
      const url = editingCategory
        ? `${API_URL}/api/card-designer/admin/categories/${editingCategory.id}`
        : `${API_URL}/api/card-designer/admin/categories`;
      const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(categoryForm) });
      if (response.ok) {
        toast({ title: "Success", description: editingCategory ? "Category updated" : "Category created" });
        setShowCategoryDialog(false);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save category');
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? Categories with templates cannot be deleted.')) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/card-designer/admin/categories/${categoryId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { toast({ title: "Category deleted" }); loadData(); }
      else { const error = await response.json(); throw new Error(error.detail || 'Failed to delete'); }
    } catch (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); }
  };

  // =================== TEMPLATES ===================
  const openTemplateDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name, category_id: template.category_id,
        price: template.price.toString(), description: template.description || '',
        front_image: template.front_image || '', back_image: template.back_image || '', is_active: template.is_active !== false
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        name: '', category_id: categories[0]?.id || '', price: '', description: '',
        front_image: '', back_image: '', is_active: true
      });
    }
    setShowTemplateDialog(true);
  };

  const handleImageUpload = async (event, side) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = await user.getIdToken();
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/card-designer/admin/templates/upload-image`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${API_URL}${data.url}`;
        setTemplateForm(prev => ({ ...prev, [side === 'front' ? 'front_image' : 'back_image']: fullUrl }));
        toast({ title: "Image uploaded" });
      }
    } catch (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally { setUploading(false); }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.price || !templateForm.category_id) {
      toast({ title: "Missing fields", description: "Name, category and price are required", variant: "destructive" }); return;
    }
    if (!templateForm.front_image || !templateForm.back_image) {
      toast({ title: "Missing images", description: "Upload both front and back images", variant: "destructive" }); return;
    }
    try {
      const token = await user.getIdToken();
      const method = editingTemplate ? 'PUT' : 'POST';
      const url = editingTemplate
        ? `${API_URL}/api/card-designer/admin/templates/${editingTemplate.id}`
        : `${API_URL}/api/card-designer/admin/templates`;
      const payload = { ...templateForm, price: parseFloat(templateForm.price) };
      const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      if (response.ok) {
        toast({ title: "Success", description: editingTemplate ? "Template updated" : "Template created" });
        setShowTemplateDialog(false);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save template');
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/card-designer/admin/templates/${templateId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { toast({ title: "Template deleted" }); loadData(); }
    } catch (error) { toast({ title: "Error", description: "Failed to delete", variant: "destructive" }); }
  };

  const handleApproval = async (templateId, action) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/card-designer/admin/templates/${templateId}/${action}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast({ title: action === 'approve' ? "Template Approved" : action === 'unapprove' ? "Template set to Pending" : "Template Rejected" });
        loadData();
      }
    } catch (error) { toast({ title: "Error", description: "Failed to update approval", variant: "destructive" }); }
  };
  const handleApproveTemplate = (id) => handleApproval(id, 'approve');
  const handleUnapproveTemplate = (id) => handleApproval(id, 'unapprove');
  const handleRejectTemplate = (id) => handleApproval(id, 'reject');

  const handleDuplicateTemplate = async (templateId) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/card-designer/admin/templates/${templateId}/duplicate`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast({ title: "Template Duplicated", description: "A copy has been created" });
        loadData();
      } else {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to duplicate');
      }
    } catch (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); }
  };

  const formatPrice = (price) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);

  if (loading) {
    return (
      <div className="min-h-[300px] bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
      </div>
    );
  }

  const content = (
    <>
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">

          {/* ============ VIEW 1: CATEGORIES GRID ============ */}
          {!selectedCategory && (
            <>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Card Designer</h1>
                  <p className="text-sm text-gray-500 mt-0.5">Select a category to manage its templates</p>
                </div>
                <Button onClick={() => openCategoryDialog()} data-testid="add-category-btn" className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" />Add Category
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="categories-grid">
                {categories.length === 0 ? (
                  <Card className="col-span-full"><CardContent className="py-16 text-center">
                    <Grid3X3 className="w-14 h-14 text-gray-200 mx-auto mb-4" />
                    <p className="text-gray-500">No categories yet.</p>
                  </CardContent></Card>
                ) : categories.map(category => {
                  const IconComp = getIconComponent(category.icon);
                  const catTemplates = templates.filter(t => t.category_id === category.id);
                  return (
                    <Card key={category.id}
                      className={`group overflow-hidden cursor-pointer transition-all hover:shadow-xl hover:-translate-y-0.5 border-2 border-transparent hover:border-amber-200 ${!category.is_active ? 'opacity-55' : ''}`}
                      onClick={() => setSelectedCategory(category)}
                      data-testid={`category-card-${category.id}`}>
                      {/* Icon hero */}
                      <div className="aspect-[4/3] bg-gradient-to-br from-amber-50 to-amber-100/60 flex items-center justify-center relative">
                        <IconComp className="w-14 h-14 text-amber-500 group-hover:scale-110 transition-transform" />
                        {!category.is_active && (
                          <Badge variant="secondary" className="absolute top-2 right-2 text-[10px]">Inactive</Badge>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">{category.name}</h3>
                        <p className="text-sm text-amber-600 mt-0.5">{catTemplates.length} template{catTemplates.length !== 1 ? 's' : ''}</p>
                        {category.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{category.description}</p>
                        )}
                        {/* Edit/Delete actions — stop propagation so card click doesn't also fire */}
                        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                          <Button size="sm" variant="outline" className="flex-1 h-7 text-xs"
                            onClick={(e) => { e.stopPropagation(); openCategoryDialog(category); }}
                            data-testid={`edit-category-${category.id}`}>
                            <Edit className="w-3 h-3 mr-1" />Edit
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 h-7"
                            onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }}
                            data-testid={`delete-category-${category.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

          {/* ============ VIEW 2: CATEGORY TEMPLATES ============ */}
          {selectedCategory && (() => {
            const IconComp = getIconComponent(selectedCategory.icon);
            return (
              <>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" className="text-gray-500 hover:text-amber-600"
                      onClick={() => setSelectedCategory(null)} data-testid="back-to-categories">
                      <ArrowLeft className="w-4 h-4 mr-1.5" />Categories
                    </Button>
                    <span className="text-gray-300">/</span>
                    <div className="flex items-center gap-2">
                      <IconComp className="w-5 h-5 text-amber-600" />
                      <h1 className="text-xl font-bold text-gray-900">{selectedCategory.name}</h1>
                      <span className="text-sm text-gray-400">({categoryTemplates.length})</span>
                    </div>
                  </div>
                  <Button onClick={() => { navigate('/admin/template-builder'); }} data-testid="add-template-btn" className="bg-amber-600 hover:bg-amber-700">
                    <Plus className="w-4 h-4 mr-2" />New Template
                  </Button>
                </div>

                {/* Templates grid */}
                {categoryTemplates.length === 0 ? (
                  <Card><CardContent className="py-16 text-center">
                    <Palette className="w-14 h-14 text-gray-200 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No templates in this category yet.</p>
                    <Button onClick={() => navigate('/admin/template-builder')} className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="w-4 h-4 mr-2" />Create First Template
                    </Button>
                  </CardContent></Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="templates-grid">
                    {categoryTemplates.map(template => (
                      <Card key={template.id} className="overflow-hidden hover:shadow-lg transition-all">
                        {/* Show thumbnail if available, otherwise live preview */}
                        {template.thumbnail_image ? (
                          <div className="aspect-[3.375/2.125] overflow-hidden bg-gray-100">
                            <img 
                              src={resolveImageUrl(template.thumbnail_image)} 
                              alt={template.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <TemplateCardPreview template={template} className="bg-gray-900 p-3" />
                        )}
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-gray-900 text-sm">{template.name}</h3>
                              <p className="text-xs text-amber-600 font-medium">{formatPrice(template.price)}</p>
                            </div>
                            <Badge className={`text-[10px] ${
                              template.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                              template.approval_status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'}`}>
                              {template.approval_status || 'pending'}
                            </Badge>
                          </div>
                          {/* Approval row */}
                          <div className="flex gap-1.5 mb-2">
                            {template.approval_status !== 'approved' ? (
                              <Button size="sm" className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700"
                                onClick={() => handleApproveTemplate(template.id)} data-testid={`approve-template-${template.id}`}>
                                <Check className="w-3 h-3 mr-1" />Approve
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" className="flex-1 h-7 text-xs text-amber-600 hover:bg-amber-50"
                                onClick={() => handleUnapproveTemplate(template.id)} data-testid={`unapprove-template-${template.id}`}>
                                <XCircle className="w-3 h-3 mr-1" />Unapprove
                              </Button>
                            )}
                            {template.approval_status !== 'rejected' && (
                              <Button size="sm" variant="outline" className="flex-1 h-7 text-xs text-red-600 hover:bg-red-50"
                                onClick={() => handleRejectTemplate(template.id)} data-testid={`reject-template-${template.id}`}>
                                <XCircle className="w-3 h-3 mr-1" />Reject
                              </Button>
                            )}
                          </div>
                          {/* Action row */}
                          <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" className="flex-1 h-7 text-xs"
                              onClick={() => navigate(`/admin/template-builder/${template.id}`)} data-testid={`edit-template-${template.id}`}>
                              <Edit className="w-3 h-3 mr-1" />Edit
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => handleDuplicateTemplate(template.id)} data-testid={`copy-template-${template.id}`} title="Duplicate">
                              <Copy className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 h-7"
                              onClick={() => handleDeleteTemplate(template.id)} data-testid={`delete-template-${template.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
      {/* ============ CATEGORY DIALOG ============ */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Category Name *</Label>
              <Input value={categoryForm.name} onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Business & Corporate" data-testid="category-name-input" />
            </div>
            <div>
              <Label>Icon *</Label>
              <IconPicker value={categoryForm.icon} onChange={(icon) => setCategoryForm(prev => ({ ...prev, icon }))} />
            </div>
            <div>
              <Label>Thumbnail Image</Label>
              <p className="text-xs text-gray-500 mb-2">Displayed in shop sidebar</p>
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                id="category-thumbnail-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => setCategoryForm(prev => ({ ...prev, thumbnail_image: ev.target.result }));
                    reader.readAsDataURL(file);
                  }
                }}
              />
              {categoryForm.thumbnail_image ? (
                <div className="relative inline-block">
                  <img src={categoryForm.thumbnail_image} alt="Thumbnail" className="w-20 h-20 object-cover rounded-lg border" />
                  <button 
                    onClick={() => setCategoryForm(prev => ({ ...prev, thumbnail_image: null }))}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-dashed"
                  onClick={() => document.getElementById('category-thumbnail-input')?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" /> Upload Thumbnail
                </Button>
              )}
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={categoryForm.description} onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input type="number" value={categoryForm.display_order} onChange={(e) => setCategoryForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="flex items-center justify-between pt-6">
                <Label>Active</Label>
                <Switch checked={categoryForm.is_active} onCheckedChange={(checked) => setCategoryForm(prev => ({ ...prev, is_active: checked }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} className="bg-amber-600 hover:bg-amber-700" data-testid="save-category-btn">{editingCategory ? 'Update' : 'Create'} Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ TEMPLATE DIALOG ============ */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingTemplate ? 'Edit Template' : 'Add New Template'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Template Name *</Label>
              <Input value={templateForm.name} onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Executive Black" data-testid="template-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={templateForm.category_id} onValueChange={(value) => setTemplateForm(prev => ({ ...prev, category_id: value }))}>
                  <SelectTrigger data-testid="template-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map(cat => {
                    const IC = getIconComponent(cat.icon);
                    return <SelectItem key={cat.id} value={cat.id}><span className="flex items-center gap-2"><IC className="w-3.5 h-3.5" />{cat.name}</span></SelectItem>;
                  })}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price (INR) *</Label>
                <Input type="number" value={templateForm.price} onChange={(e) => setTemplateForm(prev => ({ ...prev, price: e.target.value }))} placeholder="499" data-testid="template-price-input" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={templateForm.is_active} onCheckedChange={(checked) => setTemplateForm(prev => ({ ...prev, is_active: checked }))} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={templateForm.description} onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Template description..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} className="bg-amber-600 hover:bg-amber-700" data-testid="save-template-btn">{editingTemplate ? 'Update' : 'Create'} Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  if (embedded) return content;

  return (
    <Layout>
      <AdminNav />
      {content}
    </Layout>
  );
};

export default AdminCardDesigner;
